import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, CalendarRange, Users, Briefcase, Plane, Settings, LogOut, UserCircle, ChevronLeft, ChevronRight, ClipboardList, FileUp, Truck, FileSpreadsheet, Sun, Menu, X
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Badge } from './ui/badge';

export default function Layout() {
  const { user, logout, isAdmin, can } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('fd_sidebar_collapsed') === '1');
  const [criticalCount, setCriticalCount] = useState(0);
  const [totalWarnings, setTotalWarnings] = useState(0);
  // Auf dem Handy ist die Seitenleiste eine Schublade, die über den Inhalt fährt.
  const [schubladeOffen, setSchubladeOffen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('fd_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Nach jedem Seitenwechsel die Schublade zuklappen – sonst bliebe sie auf
  // dem Handy über der neuen Seite liegen.
  useEffect(() => { setSchubladeOffen(false); }, [location.pathname]);

  // Die Warnungen hängen am Dashboard-Recht. Ohne diese Prüfung fragt jede
  // Seite sie trotzdem ab – für einen Betrachter kommt dann eine Ablehnung
  // zurück, die als "Rechte geändert" gedeutet wird und die Seite neu lädt.
  // Genau das war die Endlosschleife mit der Dauermeldung auf dem Handy.
  const darfWarnungenSehen = can('dashboard', 'view');

  useEffect(() => {
    if (!darfWarnungenSehen) {
      setCriticalCount(0);
      setTotalWarnings(0);
      return;
    }
    const loadBadge = async () => {
      try {
        const r = await api.get('/warnings');
        const critical = (r.data.over_hours?.length || r.data.double_bookings?.length || 0)
          + (r.data.absence_conflicts?.length || 0)
          + (r.data.over_budget_projects?.length || 0);
        const notice = (r.data.overdue_projects?.length || 0) + (r.data.upcoming_end_projects?.length || 0)
          + (r.data.expiring_contracts?.length || 0)
          // Eingeplant am freien Tag: zählt als Hinweis, nicht als kritisch –
          // sonst stünde die Zahl in der Seitenleiste rot, obwohl nichts kaputt ist.
          + (r.data.non_workday_assignments?.length || 0);
        setCriticalCount(critical);
        setTotalWarnings(critical + notice);
      } catch {}
    };
    loadBadge();
    const iv = setInterval(loadBadge, 30000);
    return () => clearInterval(iv);
  }, [location.pathname, darfWarnungenSehen]);

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, badge: totalWarnings, badgeVariant: totalWarnings > 0 ? (criticalCount > 0 ? 'critical' : 'notice') : null, allow: can('dashboard', 'view') },
    { to: '/tagesansicht', label: 'Tagesansicht', icon: Sun, allow: can('day_view', 'view') },
    { to: '/plantafel', label: 'Plantafel', icon: CalendarRange, badge: criticalCount, badgeVariant: criticalCount > 0 ? 'critical' : null, allow: can('plantafel', 'view') },
    { to: '/geraete-plantafel', label: 'Geräte-Plantafel', icon: Truck, allow: can('device_plantafel', 'view') },
    { to: '/mitarbeiter', label: 'Mitarbeiter', icon: Users, allow: can('employees', 'view') },
    { to: '/geraete', label: 'Geräte', icon: Truck, allow: can('devices', 'view') },
    { to: '/projekte', label: 'Projekte', icon: Briefcase, allow: can('projects', 'view') },
    { to: '/abwesenheiten', label: 'Abwesenheiten', icon: Plane, allow: can('absences', 'view') },
    { to: '/anwesenheit', label: 'Anwesenheit', icon: FileSpreadsheet, allow: can('attendance_timesheets', 'view') },
    { to: '/zeitimport', label: 'Zeitimport', icon: FileUp, allow: can('time_import', 'view') },
    { to: '/einstellungen', label: 'Einstellungen', icon: Settings, allow: isAdmin },
    { to: '/aktivitaet', label: 'Aktivität', icon: ClipboardList, allow: isAdmin },
  ];

  const sichtbareNav = navItems.filter(n => n.allow);

  return (
    <div className="flex min-h-screen bg-[#f6f8fb]">
      {/* Kopfleiste nur auf kleinen Bildschirmen */}
      <header className="no-print md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-slate-200
                         flex items-center gap-3 px-3"
              style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3.5rem + env(safe-area-inset-top))' }}>
        <button
          onClick={() => setSchubladeOffen(true)}
          className="p-2 -ml-1 rounded-lg active:bg-slate-100 text-slate-700"
          aria-label="Menü öffnen"
          data-testid="mobile-menu-button"
        >
          <Menu size={22} />
        </button>
        <img src={`${process.env.PUBLIC_URL || ''}/logo.svg`} alt="" className="w-8 h-8 rounded-md flex-shrink-0" />
        <div className="font-semibold text-sm truncate font-display">Einsatzplanung</div>
      </header>

      {/* Abdunkelung hinter der Schublade */}
      {schubladeOffen && (
        <div className="md:hidden fixed inset-0 z-40 bg-slate-900/40"
             onClick={() => setSchubladeOffen(false)} aria-hidden="true" />
      )}

      <aside
        className={`no-print fixed inset-y-0 left-0 z-50 bg-white border-r border-slate-200 flex flex-col
                    transition-transform duration-200 w-[17rem]
                    ${schubladeOffen ? 'translate-x-0' : '-translate-x-full'}
                    md:translate-x-0 md:transition-all ${collapsed ? 'md:w-16' : 'md:w-60'}`}
        data-testid="app-sidebar"
      >
        <div className="h-14 flex items-center justify-between px-3 border-b border-slate-200">
          {!collapsed ? (
            <div className="flex items-center gap-2 min-w-0">
              <img src={`${process.env.PUBLIC_URL || ''}/logo.svg`} alt="" className="w-8 h-8 rounded-md" />
              <div className="font-semibold text-sm truncate font-display">Friedrichs Dach</div>
            </div>
          ) : (
            <img src={`${process.env.PUBLIC_URL || ''}/logo.svg`} alt="" className="w-8 h-8 rounded-md mx-auto" />
          )}
          <button
            onClick={() => setSchubladeOffen(false)}
            className="md:hidden p-2 rounded-lg active:bg-slate-100 text-slate-600"
            aria-label="Menü schließen"
          >
            <X size={20} />
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`hidden md:block p-1.5 rounded-md hover:bg-slate-100 text-slate-600 ${collapsed ? 'absolute right-[-12px] top-4 bg-white border border-slate-200 shadow-sm' : ''}`}
            data-testid="sidebar-collapse-button"
            title={collapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>
        <nav className="flex-1 py-2 space-y-0.5">
          {navItems.filter(n => n.allow).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 mx-2 px-3 py-3 md:px-2 md:py-2 rounded-lg text-[15px] md:text-sm font-semibold ${
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100 active:bg-slate-100'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={20} className="flex-shrink-0 md:w-[18px] md:h-[18px]" />
                <span className={`truncate ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                {!collapsed && item.badge > 0 && (
                  <Badge
                    className={`ml-auto text-white ${item.badgeVariant === 'critical' ? 'bg-rose-600 hover:bg-rose-600' : 'bg-amber-600 hover:bg-amber-600'}`}
                    data-testid={`nav-badge-${item.label.toLowerCase()}`}
                  >
                    {item.badge}
                  </Badge>
                )}
                {collapsed && item.badge > 0 && (
                  <span className={`absolute top-0 right-0 -mt-1 -mr-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white rounded-full ${item.badgeVariant === 'critical' ? 'bg-rose-600' : 'bg-amber-600'}`}>
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-2 space-y-1">
          <NavLink
            to="/profil"
            className={({ isActive }) => `flex items-center gap-3 px-2 py-2 rounded-md text-sm font-semibold ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`}
            data-testid="nav-profile"
            title={collapsed ? 'Profil & Passwort ändern' : undefined}
          >
            <UserCircle size={18} />
            {!collapsed && (
              <div className="flex flex-col min-w-0 leading-tight">
                <span className="truncate text-xs text-slate-500 font-normal">Profil</span>
                <span className="truncate">{user?.first_name} {user?.last_name}</span>
              </div>
            )}
          </NavLink>
          <button
            onClick={() => logout()}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-rose-50 hover:text-rose-700"
            data-testid="logout-button"
            title="Abmelden"
          >
            <LogOut size={18} />
            {!collapsed && <span>Abmelden</span>}
          </button>
        </div>
      </aside>
      <main className={`flex-1 transition-all duration-200 ${collapsed ? 'md:pl-16' : 'md:pl-60'}`}>
        {/* Abstand für die feste Kopfleiste auf dem Handy */}
        <div className="h-14 md:hidden" style={{ height: 'calc(3.5rem + env(safe-area-inset-top))' }} />
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// PageTitle wurde entfernt – statt dessen sparen wir vertikalen Platz auf der Plantafel.
